import type { ReactNode } from 'react'
import ShieldCheck from 'lucide-react/dist/esm/icons/shield-check'

import { cn } from '@/lib/utils'

/** Keeps the welcome block the same height in skeleton and loaded states to prevent layout shift. */
export function DashboardWelcomeSection({ children }: { children: ReactNode }) {
  return (
    <div className="mb-5 min-h-[9.5rem] lg:mb-6 lg:min-h-[10rem]">{children}</div>
  )
}

interface DashboardWelcomeHeroProps {
  displayName: string
  groupName: string
  description: string
}

/** Top-of-page welcome card — white block with a subtle brand accent. */
export function DashboardWelcomeHero({
  displayName,
  groupName,
  description,
}: DashboardWelcomeHeroProps) {
  return (
    <DashboardWelcomeSection>
      <div className="overflow-hidden rounded-2xl border border-border/60 bg-white shadow-[0_2px_16px_rgba(0,0,0,0.04)]">
        <div className="h-[3px] bg-primary-to-purple-gradient" aria-hidden="true" />

        <div className="p-5 sm:p-6">
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3 sm:gap-4">
              <h1 className="min-w-0 text-lg font-semibold leading-tight tracking-tight text-foreground sm:text-xl">
                Welcome, {displayName}
              </h1>
              <div className="inline-flex shrink-0 items-center gap-2 rounded-full border border-border/50 bg-white px-3.5 py-1.5 text-xs font-semibold text-foreground shadow-sm">
                <ShieldCheck className="h-4 w-4 text-success-border" aria-hidden="true" />
                Verified portal
              </div>
            </div>
            <div className="inline-flex max-w-full items-center rounded-full border border-border/50 bg-white px-3.5 py-1.5 text-sm shadow-sm">
              <span className="text-muted-foreground">Verification for</span>
              <span className="ml-1.5 truncate font-semibold text-foreground">{groupName}</span>
            </div>
          </div>

          <div className="mt-5 border-t border-border/50 pt-5">
            <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">{description}</p>
          </div>
        </div>
      </div>
    </DashboardWelcomeSection>
  )
}

export function DashboardSectionHeading({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <h2
      className={cn(
        'flex h-5 items-center text-sm font-semibold uppercase leading-none tracking-wide text-foreground',
        className,
      )}
    >
      {children}
    </h2>
  )
}

/** Invisible heading that aligns the sidebar with the main column section titles on large screens. */
export function DashboardSidebarHeader() {
  return (
    <DashboardSectionHeading
      aria-hidden="true"
      className="pointer-events-none hidden lg:flex"
    >
      <span className="invisible">Overview</span>
    </DashboardSectionHeading>
  )
}

/** Fixed height for the drag-and-drop upload zone. */
export const DOCUMENT_DROP_ZONE_HEIGHT = 'h-[8.5rem]'
